import React, { useState, useEffect } from 'react';
import { When } from 'react-if';
import { useSelector } from 'react-redux';
import {
    Card,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import Loading from '../../loading';
import NoData from '../../no-data';
import { Label } from '../../label';
import { RootState } from '../../../store';
import { PomBannerService } from '../../../services/pomBanner.service';
import { BANNER_COLUMNS } from '../../../builders/banners-builders';
import { PomBannerTypes } from '../../../types/pomBanner.types';

const TYPE_COLORS: Record<PomBannerTypes.BannerType, 'info' | 'success' | 'warning' | 'error'> = {
    info: 'info',
    success: 'success',
    warning: 'warning',
    error: 'error',
};

export const BannerList: React.FC = () => {
    const {
        pagination: { total, items },
        loading,
    } = useSelector((state: RootState) => state.bannersAdmin);
    const navigate = useNavigate();

    const [pagination, setPagination] = useState({ page: 0, perPage: 25 });

    useEffect(() => {
        PomBannerService.fetchMany({
            page: pagination.page + 1,
            perPage: pagination.perPage,
        });
    }, [pagination.perPage, pagination.page]);

    const handleDelete = async (id: string, title: string) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

        await PomBannerService.remove(id, (isValid, msg) => {
            toast(msg, { autoClose: 1500, type: isValid ? 'success' : 'error' });
        });
    };

    return (
        <Card sx={{ mt: 2 }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {BANNER_COLUMNS.map((column) => (
                                <TableCell key={column.field}>
                                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                        {column.headerName}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <When condition={!loading?.fetchMany}>
                        <TableBody>
                            {items?.map((banner) => (
                                <TableRow
                                    key={banner.id}
                                    sx={{
                                        ':hover': {
                                            backgroundColor: '#F0F0F0',
                                            cursor: 'pointer',
                                        },
                                    }}
                                    onClick={() => navigate(`/banners/${banner.id}/edit`)}
                                >
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {banner.title}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Label variant="ghost" color={TYPE_COLORS[banner.type]}>
                                            {banner.type}
                                        </Label>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {banner.isActive ? 'Yes' : 'No'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {banner.targetAllOrgs
                                                ? 'All Orgs'
                                                : `${banner.targetSubscriptionIds?.length || 0} orgs`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {banner.priority}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {banner.startsAt
                                                ? dayjs(banner.startsAt).format('MM/DD/YYYY hh:mm A')
                                                : '----'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {banner.expiresAt
                                                ? dayjs(banner.expiresAt).format('MM/DD/YYYY hh:mm A')
                                                : '----'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {banner.createdAt
                                                ? dayjs(banner.createdAt).format('MM/DD/YYYY hh:mm A')
                                                : '----'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <IconButton
                                            size="small"
                                            onClick={() => navigate(`/banners/${banner.id}/edit`)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(banner.id, banner.title)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </When>
                </Table>

                <When condition={loading.fetchMany === false && (items?.length ?? 0) < 1}>
                    <NoData
                        buttonText={null}
                        onClick={() => {}}
                        title="No banners found"
                        description="No banners have been created yet."
                    />
                </When>

                <When condition={loading?.fetchMany}>
                    <Loading />
                </When>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={total || 0}
                rowsPerPage={pagination.perPage}
                page={pagination.page}
                onPageChange={(_e, p) => setPagination((pag) => ({ ...pag, page: p }))}
                onRowsPerPageChange={(e) =>
                    setPagination((pag) => ({
                        ...pag,
                        perPage: parseInt(e.target.value, 10),
                    }))
                }
            />
        </Card>
    );
};
